import React, {
  ChangeEvent,
  useCallback,
  useRef,
  useState,
  useEffect
} from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Theme } from '@material-ui/core'
import { Title } from '@siftscience/focus-components/text'
import { Button, ButtonGroup } from '@siftscience/focus-components/button'
import { Switch } from '@siftscience/focus-components/switch'
import { Override, Rollback } from '@siftscience/focus-components/icons'
import { useEditor } from '@tiptap/react'
import { Node } from '@tiptap/core'
import { Tooltip, TooltipTrigger } from '@siftscience/focus-components/tooltip'
import debounce from 'lodash.debounce'
import AddRow from './add-row'
import ReasonCodesRow from './reason-codes-row'
import CustomRuleSetRow from './custom-rule-set-row'
import MerchantsAffectedRow from './merchants-affected-row'
import { DataField, TemplateDTO } from '../dto/template'
import { useTemplateContext } from '../context/template-context'
import { useDataFieldContext } from '../context/data-field-context'
import { useCurrentUser } from '../../../contexts/CurrentUserContext'
import { NONE_MERCHANT, useMerchantContext } from '../context/merchant-context'
import EditorComponent from './editor'
import {
  getEditorConfig,
  insertDfNode,
  insertStNode
} from './editor/utils/general'
import DisputesAffectedModal, {
  DisputesAffectedModalProps
} from '../common-components/disputes-affected-modal'
import { useDisputesAffected } from '../hooks/useDisputesAffected'
import { VerificationWarningFilled } from '@siftscience/focus-components/icons/VerificationWarningFilled'

const ONCHANGE_DEBOUNCE_TIME = 400

const REASON_CODES_EMPTY_WARNING_TEXT =
  'Reason codes are empty. Template will not be displayed in the workspace.'

interface StyleProps {
  isFullWidth: boolean
}

const useStyles = makeStyles<Theme, StyleProps>(() => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flex: 1,
    gap: '8px',
    height: '100%',
    maxWidth: props => (props.isFullWidth ? '100%' : 'calc(50% - 8px)') // 8px for half the gap
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '8px'
  },
  headerRight: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px'
  },
  innerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: 1,
    height: '100%',
    border: '1px solid #E0E0E0',
    borderRadius: '6px',
    backgroundColor: '#FFFFFF'
  },
  actionButton: {
    display: 'flex',
    flexDirection: 'row'
  }
}))

interface TemplateEditorProps {
  title: string
  readonly?: boolean
  isFullWidth?: boolean
  template: TemplateDTO
}

const TemplateEditor = ({
  title,
  readonly,
  template,
  isFullWidth
}: TemplateEditorProps) => {
  const [modalState, setModalState] = useState<
    { isOpen: boolean } & DisputesAffectedModalProps
  >(null)
  const [isRawHtmlView, setIsRawHtmlView] = useState(false)
  const {
    relevantTemplates,
    templatesHash,
    updatedEvidenceRules,
    removeTemplate,
    overrideTemplate,
    onRevertOverride,
    setUpdatedTemplateContent,
    onToggleActiveStatus,
    toggleTemplateHiddenStatus,
    toggleTemplateRenderingExcludedStatus
  } = useTemplateContext()
  const { dataFieldsHash, addDataFieldToTemplate } = useDataFieldContext()
  const { selectedMerchant } = useMerchantContext()
  const { count } = useDisputesAffected(
    template?.templateId,
    selectedMerchant.id
  )
  const { currentUser } = useCurrentUser()
  const cleanDocRef = useRef<Node | null>(null)

  const triggerTemplateContentUpdate = useCallback(
    debounce(setUpdatedTemplateContent, ONCHANGE_DEBOUNCE_TIME),
    [setUpdatedTemplateContent]
  )

  const editor = useEditor(
    getEditorConfig(
      template?.content,
      readonly,
      cleanDocRef,
      triggerTemplateContentUpdate
    )
  )

  const classes = useStyles({ isFullWidth })

  useEffect(() => {
    if (editor && template) {
      editor.commands.setTemplateId(template.templateId)
    }
  }, [editor, template])

  const onHtmlEditorChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event?.target?.value
    const isDirty = value !== template?.content
    setUpdatedTemplateContent({ editor: null, isDirty, rawHtmlContent: value })
  }

  const onCodeViewClick = () => {
    setIsRawHtmlView(true)
    setUpdatedTemplateContent({
      editor: null,
      isDirty: false,
      rawHtmlContent: null
    })
  }

  const onEditorViewClick = () => {
    setIsRawHtmlView(false)
    setUpdatedTemplateContent({
      editor: null,
      isDirty: false,
      rawHtmlContent: null
    })
  }

  const onAddSubtemplate = (subtemplateId: number) => {
    const subtemplate = relevantTemplates?.[template.templateId]?.find(
      templ => templ.templateId === subtemplateId
    )

    if (!subtemplate) {
      return
    }

    insertStNode(editor, subtemplate.name)
  }

  const onAddDataField = (dataFieldId: number) => {
    const dataField = dataFieldsHash.get(dataFieldId)
    insertDfNode(editor, dataField.datasetPath)
    addDataFieldToTemplate(dataField, false)
  }

  const onCreateDataField = (dataField: Partial<DataField>) => {
    insertDfNode(editor, dataField.datasetPath)
    addDataFieldToTemplate(dataField, true)
    insertDfNode(editor, dataField.datasetPath || dataField.dataset_path)
  }

  const openModal = (onSave: () => void, templateId?: number) => {
    setModalState({
      isOpen: true,
      templateId: templateId || template.templateId,
      onClose: () => setModalState(null),
      onSave
    })
  }

  const onDeleteTemplate = () => {
    openModal(() => removeTemplate(template.templateId))
  }

  const onOverrideTemplate = () => {
    openModal(() => overrideTemplate(template))
  }

  const onRevertToDefault = () => {
    openModal(() => onRevertOverride())
  }

  const onToggleActive = () => {
    const templateId = template?.active
      ? template.templateId
      : template.overridenTemplateId
    openModal(() => onToggleActiveStatus(), templateId)
  }

  const isGPDEditAllowed =
    currentUser.hasAccess(['Super Administrator']) && !template.merchantId
  const isOverriddenForCurrentMerchant = !!template.overridenByMerchants?.find(
    name => name === selectedMerchant.name
  )
  const isEditable =
    (!readonly || isGPDEditAllowed) && !isOverriddenForCurrentMerchant

  const onToggleHideTemplate = () => {
    if (isEditable) {
      toggleTemplateHiddenStatus(template.templateId)
    }
  }

  const onToggleTemplateRenderingExclude = () => {
    if (isEditable) {
      toggleTemplateRenderingExcludedStatus(template.templateId)
    }
  }

  const isMerchantSpecific = template?.merchantSpecific
  const isDeleteAllowed =
    template?.overridenByMerchants?.length === 0 && // Not overridden
    template.childTemplateIds?.length === 0 && // No subtemplates
    count === 0 // No affected disputes by delete

  const isOverridenTemplate = !!template?.merchantId
  const isNoneMerchantSelected = selectedMerchant?.id === NONE_MERCHANT.id
  const isOverrideTemplateVisible =
    !template.merchantId &&
    !isOverriddenForCurrentMerchant &&
    !isNoneMerchantSelected &&
    !isMerchantSpecific
  const isActiveSwitchVisible = !readonly && !!template.overridenTemplateId
  const isRevertToDefaultVisible =
    !readonly && isOverridenTemplate && !isMerchantSpecific
  const isDeleteVisible =
    isGPDEditAllowed || (template.merchantId && !template.overridenTemplateId)
  const parentTemplatesList = template?.parentTemplateIds
    ?.map(templateId => templatesHash.get(templateId)?.displayName)
    .join(', ')

  const templateReasonCodes =
    template?.evidenceRules?.[0]?.reason_code_categories
  const reasonCodes = readonly
    ? templateReasonCodes
    : updatedEvidenceRules?.reason_code_categories || templateReasonCodes

  return (
    <>
      <div className={classes.wrapper}>
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <Title size="medium">{title}</Title>
            {reasonCodes?.length === 0 ? (
              <Tooltip
                content={REASON_CODES_EMPTY_WARNING_TEXT}
                placement="top"
              >
                <VerificationWarningFilled color="#EB3B2E" />
              </Tooltip>
            ) : null}
            {isActiveSwitchVisible && (
              <Switch
                key={`template-${template?.templateId}-active-${template?.active}`}
                checked={!!template?.active}
                onChange={onToggleActive}
              />
            )}
          </div>
          <div className={classes.headerRight}>
            {isRevertToDefaultVisible && (
              <Button
                variant="secondary"
                className={classes.actionButton}
                lined
                onClick={onRevertToDefault}
              >
                <Rollback />
                <Title size="xsmall">Revert to Default</Title>
              </Button>
            )}
            {isOverrideTemplateVisible && (
              <Button
                variant="primary"
                className={classes.actionButton}
                lined
                onClick={onOverrideTemplate}
              >
                <Override />
                <Title size="xsmall">Override Template</Title>
              </Button>
            )}
            {isEditable ? (
              <Button lined variant="primary" onClick={onToggleHideTemplate}>
                {template.hidden ? 'Hidden, show' : 'Visible, hide'}
              </Button>
            ) : null}
            {isEditable ? (
              <Button
                lined
                variant="primary"
                onClick={onToggleTemplateRenderingExclude}
              >
                {template.renderingExcluded
                  ? 'Excluded, include'
                  : 'Included, exclude'}
              </Button>
            ) : null}
            {isDeleteVisible &&
              (parentTemplatesList ? (
                <TooltipTrigger
                  content={`Subtemplate is used in other templates. Please delete it from ${parentTemplatesList} content first.`}
                >
                  <Button
                    variant="danger"
                    disabled
                    className={classes.actionButton}
                    onClick={onDeleteTemplate}
                  >
                    <Title size="xsmall">Delete</Title>
                  </Button>
                </TooltipTrigger>
              ) : (
                <Button
                  variant="danger"
                  disabled={!isDeleteAllowed}
                  className={classes.actionButton}
                  onClick={onDeleteTemplate}
                >
                  <Title size="xsmall">Delete</Title>
                </Button>
              ))}
            <ButtonGroup>
              <Button
                lined
                variant={!isRawHtmlView ? 'primary' : 'secondary'}
                onClick={onEditorViewClick}
              >
                Editor
              </Button>
              <Button
                lined
                variant={isRawHtmlView ? 'primary' : 'secondary'}
                onClick={onCodeViewClick}
              >
                Code
              </Button>
            </ButtonGroup>
          </div>
        </div>
        <div className={classes.innerWrapper}>
          <EditorComponent
            editor={editor}
            readonly={readonly}
            isRawHtmlView={isRawHtmlView}
            template={template}
            onHtmlEditorChange={onHtmlEditorChange}
          />
          <div>
            {isGPDEditAllowed && <MerchantsAffectedRow template={template} />}
            {!readonly && (
              <AddRow
                onAddSubtemplate={onAddSubtemplate}
                onAddDataField={onAddDataField}
                onCreateDataField={onCreateDataField}
                template={template}
              />
            )}
            <ReasonCodesRow readonly={readonly} template={template} />
            <CustomRuleSetRow readonly={readonly} template={template} />
          </div>
        </div>
      </div>
      {modalState?.isOpen ? (
        <DisputesAffectedModal
          templateId={modalState.templateId}
          onClose={modalState.onClose}
          onSave={modalState.onSave}
        />
      ) : null}
    </>
  )
}

export default TemplateEditor
